import type {
  PriceListCategory,
  PriceListPricingMode,
  PriceListUnit,
} from '@xop/dashboard-shared';

import api from './api';

// Product types
export interface Product {
  id: number;
  name: string;
  code?: string;
  created_at: string;
  updated_at: string;
  features_count: number;
  created_by: {
    id: number;
    name: string;
  } | null;
  features?: ProductFeature[];
}

/**
 * One feature of a product — and what it COSTS. The price book merged into the
 * catalogue on 2026-09-13, so every picker that reads this shape (the invoice
 * cascade, the agreement modal, the licence builder) gets the price with the
 * name and there is no second list to consult.
 */
export interface ProductFeature {
  id: number;
  name: string;
  code?: string;
  /** The printed part number, falling back server-side to the composed
   * `<product code>-<feature code>` so the field is never blank. */
  part_number?: string;
  description?: string;
  category: PriceListCategory;
  unit: PriceListUnit;
  pricing_mode: PriceListPricingMode;
  /** Integer cents. Always 0 unless `pricing_mode === "fixed"`. */
  price_cents: number;
  percent_rate: number | null;
  currency: string;
  notes?: string;
  active: boolean;
  sort_order?: number;
  /** The server's verdict on "has anybody priced this?". */
  priced?: boolean;
  created_at: string;
  updated_at: string;
  product_id?: number;
  product: {
    id: number;
    name: string;
  };
  created_by?: {
    id: number;
    name: string;
  };
}

// API functions
export const productsApi = {
  // Flat product list for the Agreement modal's Product picker. Hits
  // the SAME working endpoint System Admin ▸ Products uses
  // (GET /api/crm/products → { products: [...] }). The old
  // getProductHierarchy() below targets /products/invoice_products
  // (the invoice cascade), which has no dashboard proxy route and so
  // resolved to an empty list → the Agreement Product dropdown was
  // always "None".
  async listProducts(): Promise<Product[]> {
    try {
      const response = await api.get('/products');
      return response.data?.products ?? response.data?.data ?? [];
    } catch (error: any) {
      console.error('❌ Error fetching products list:', error?.response?.data || error?.message || error);
      return [];
    }
  },

  // Get hierarchical product data for cascading dropdowns
  // TWO levels since 2026-09-13 (product → feature); each feature carries its
  // own price, so a picker fills a line from one pick.
  async getProductHierarchy(): Promise<{
    products: Product[];
    productFeatures: { [productId: number]: ProductFeature[] };
  }> {
    try {
      const response = await api.get('/products/invoice_products');
      
      
      if (response.data?.success && response.data?.hierarchy) {
        const hierarchy = response.data.hierarchy;
        
        
        return {
          products: hierarchy.products || [],
          productFeatures: hierarchy.product_features || {},
        };
      } else {
        console.warn('⚠️ Unexpected API response structure:', response.data);
        return { products: [], productFeatures: {} };
      }
    } catch (error: any) {
      console.error('❌ Error fetching product hierarchy:', error);
      console.error('Error details:', error?.response?.data || error?.message || 'Unknown error');
      return { products: [], productFeatures: {} };
    }
  }
};
